import React, { useState, useEffect } from "react";
import {
  Dialog,
  Flex,
  Text,
  CloseIcon,
  Button,
  Loader,
} from "@fluentui/react-northstar";
import { useTranslation } from "react-i18next";
import { api } from "../../../../Assets/api/api";

const SharedSchoolInfoDialog = ({ open, onClose, schoolInfo }) => {
  const { t } = useTranslation();
  const MainTranslation = t("trustedNetwork").schools;
  const translation = MainTranslation.schoolInfoDialog;

  const [branchCount, setBranchCount] = useState(null);
  const [isLoadingBranches, setIsLoadingBranches] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect screen size for responsive behavior
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Check for medium screens (769px - 1099px)
  const [isMediumScreen, setIsMediumScreen] = useState(false);

  useEffect(() => {
    const checkMediumScreen = () => {
      const width = window.innerWidth;
      setIsMediumScreen(width > 768 && width <= 1099);
    };

    checkMediumScreen();
    window.addEventListener("resize", checkMediumScreen);

    return () => window.removeEventListener("resize", checkMediumScreen);
  }, []);

  // Fetch branch count when dialog opens
  useEffect(() => {
    const fetchBranchCount = async () => {
      if (!open || !schoolInfo || !schoolInfo.schoolId) {
        setBranchCount(null);
        return;
      }

      setIsLoadingBranches(true);
      try {
        const response = await api.get(
          `/api/school/get-branches?slug=${schoolInfo.schoolId}`
        );

        if (response.status === 200) {
          const branchesData = response.data?.data || response.data || [];
          const branches = Array.isArray(branchesData) ? branchesData : [];
          setBranchCount(branches.length);
        }
      } catch (error) {
        console.error("Error fetching branch count:", error);
        setBranchCount(0);
      } finally {
        setIsLoadingBranches(false);
      }
    };

    fetchBranchCount();
  }, [open, schoolInfo]);

  const infoRows = [
    { label: translation.schoolName, value: schoolInfo?.schoolName },
    { label: translation.superAdminName, value: schoolInfo?.superAdminName },
    { label: translation.superAdminEmail, value: schoolInfo?.superAdminEmail },
    { label: translation.contactNumber, value: schoolInfo?.contactNumber },
    { label: translation.address, value: schoolInfo?.schoolAddress },
    { label: translation.totalStudents, value: schoolInfo?.studentsCount },
    { label: translation.totalTeachers, value: schoolInfo?.teachersCount },
    {
      label: translation.totalBranches,
      value: isLoadingBranches
        ? translation.loading
        : branchCount !== null
        ? branchCount
        : translation.notAvailable,
    },
  ];

  // Responsive dialog styles
  const dialogStyle = {
    minWidth: isMobile ? "90vw" : "400px",
    maxWidth: isMobile ? "95vw" : "500px",
    width: isMobile ? "90vw" : "auto",
    maxHeight: isMobile ? "90vh" : "80vh",
    margin: (isMobile || isMediumScreen) ? "15px" : "auto",
  };

  // Responsive content padding
  const contentPadding = isMobile ? "padding.medium" : "padding.medium";

  // Responsive text sizes
  const headerSize = isMobile ? "medium" : "large";
  const labelSize = isMobile ? "small" : "medium";
  const valueSize = isMobile ? "small" : "medium";

  return (
    <Dialog
      open={open}
      onOpen={() => {}}
      onCancel={onClose}
      header={{
        content: (
          <Text 
            content={translation.header} 
            weight="bold" 
            size={headerSize}
            style={{ 
              fontSize: isMobile ? "16px" : "18px",
              lineHeight: isMobile ? "1.4" : "1.5"
            }}
          />
        ),
      }}
      style={dialogStyle}
      content={
        <Flex 
           column 
           gap={isMobile ? "gap.small" : "gap.small"} 
           padding={contentPadding}
           style={{
             overflowY: isMobile ? "auto" : "visible",
             maxHeight: isMobile ? "calc(90vh - 120px)" : "none",
             padding: (isMobile || isMediumScreen) ? "20px 24px" : "16px"
           }}
         >
          {infoRows.map((row, index) => (
            <Flex key={index} column gap="gap.smaller">
              <Text 
                content={row.label} 
                weight="semibold" 
                color="brand"
                size={labelSize}
                style={{
                  fontSize: isMobile ? "13px" : "14px",
                  lineHeight: "1.4"
                }}
              />
              <Text
                content={row.value || translation.notAvailable}
                size={valueSize}
                style={{
                  color: row.value ? "inherit" : "#666",
                  fontStyle: row.value ? "normal" : "italic",
                  fontSize: isMobile ? "12px" : "14px",
                  lineHeight: "1.4",
                  wordBreak: "break-word",
                  overflowWrap: "break-word"
                }}
              />
              {index < infoRows.length - 1 && (
                <div
                  style={{
                    height: "1px",
                    background: "#E1E1E1",
                    margin: isMobile ? "6px 0" : "8px 0",
                  }}
                />
              )}
            </Flex>
          ))}
        </Flex>
      }
      headerAction={{
        icon: <CloseIcon />,
        title: translation.close,
        onClick: onClose,
        style: {
          minWidth: isMobile ? "32px" : "auto",
          minHeight: isMobile ? "32px" : "auto"
        }
      }}
    />
  );
};

export default SharedSchoolInfoDialog;
